import { MouseEventHandler, ReactNode } from 'react';
import { classNames } from '../../utils';

export type IconButtonSize =
  | 'x-small'
  | 'small'
  | 'normal'
  | 'large'
  | 'x-large';
export type IconButtonVariant = 'primary' | 'secondary' | 'ternary';
export type IconButtonColor = 'default' | 'primary' | 'positive' | 'negative';

interface Props {
  variant?: IconButtonVariant;
  size?: IconButtonSize;
  color?: IconButtonColor;
  disabled?: boolean;
  onClick?: MouseEventHandler<HTMLButtonElement>;
  className?: string;
  children: ReactNode;
}

export default function IconButton({
  variant = 'primary',
  size = 'normal',
  color = 'default',
  disabled,
  onClick,
  className,
  children,
}: Props) {
  return (
    <>
      <button
        type="button"
        disabled={disabled}
        onClick={onClick}
        className={classNames(
          'rounded-full',
          {
            'x-small': 'p-1',
            'small': 'p-1.5',
            'normal': 'p-2',
            'large': 'p-2.5',
            'x-large': 'p-3',
          }[size],
          'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2',
          {
            primary: 'text-white shadow-sm',
            secondary: 'ring-1 ring-inset hover:text-white',
            ternary: '',
          }[variant],

          variant === 'primary'
            ? {
                default:
                  'bg-indigo-600 hover:bg-indigo-500 focus-visible:outline-indigo-600 disabled:bg-gray-100 disabled:text-gray-400',
                primary:
                  'bg-indigo-600 hover:bg-indigo-500 focus-visible:outline-indigo-600 disabled:bg-gray-100 disabled:text-gray-400',
                positive:
                  'bg-green-600 hover:bg-green-500 focus-visible:outline-green-600 disabled:bg-gray-100 disabled:text-gray-400',
                negative:
                  'bg-red-600 hover:bg-red-500 focus-visible:outline-red-600 disabled:bg-gray-100 disabled:text-gray-400',
              }[color]
            : '',
          variant === 'secondary'
            ? {
                default:
                  'text-gray-900 ring-gray-300 hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-400 disabled:ring-gray-200',
                primary:
                  'text-indigo-600 ring-indigo-600 hover:bg-indigo-500 disabled:bg-indigo-50 disabled:text-indigo-300 disabled:ring-indigo-100',
                positive:
                  'text-emerald-600 ring-emerald-600 hover:bg-emerald-500 disabled:bg-emerald-50 disabled:text-emerald-300 disabled:ring-emerald-100',
                negative:
                  'text-red-600 ring-red-600 hover:bg-red-500 disabled:bg-red-50 disabled:text-red-300 disabled:ring-red-100',
              }[color]
            : '',
          variant === 'ternary'
            ? {
                default:
                  'text-gray-900 hover:bg-gray-100 disabled:bg-transparent disabled:text-gray-200',
                primary:
                  'text-indigo-600 hover:bg-indigo-100 disabled:bg-transparent disabled:text-indigo-200',
                positive:
                  'text-green-600 hover:bg-green-100 disabled:bg-transparent disabled:text-green-200',
                negative:
                  'text-red-600 hover:bg-red-100 disabled:bg-transparent disabled:text-red-200',
              }[color]
            : '',
          'focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 focus:ring-offset-gray-100',
          className,
        )}
      >
        {children}
      </button>
    </>
  );
}
